/**
 * Auto Quote Submission Hook
 *
 * Business logic for one-click quote submission from Kakao alimtalk.
 * Auto-generates quote response from URL parameters (price) and submits.
 */

import { useState, useCallback } from "react";
import { useSubmitAutoQuote } from "../../infrastructure/api/autoQuoteApi";
import { useQuoteRequestData } from "./useQuoteRequestData";
import type { CreateQuoteResponseVersionDto } from "@semo/api-contracts";

export interface UseAutoQuoteSubmissionParams {
  quoteRequestId: string;
  unitPrice: number;
  supplierId: string;
}

export interface UseAutoQuoteSubmissionReturn {
  submitQuote: () => Promise<void>;
  isSubmitting: boolean;
  isFetching: boolean;
  error: Error | null;
  chatRoomId: string | null;
}

/**
 * Hook for auto-submitting quote from Kakao alimtalk button
 *
 * Flow:
 * 1. Auto-generate DTO from URL params (unit price)
 * 2. Submit via existing API endpoint
 * 3. Extract chat room ID for redirect
 * 4. Handle errors gracefully
 *
 * @param params - Quote request ID, unit price from URL, supplier ID
 * @returns Submission state and submit function
 */
export const useAutoQuoteSubmission = ({
  quoteRequestId,
  unitPrice,
}: UseAutoQuoteSubmissionParams): UseAutoQuoteSubmissionReturn => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [chatRoomId, setChatRoomId] = useState<string | null>(null);

  // Fetch quote request data
  const {
    quoteRequest,
    isLoading: isFetching,
    error: fetchError,
  } = useQuoteRequestData(quoteRequestId);

  const submitAutoQuoteMutation = useSubmitAutoQuote({
    mutation: {
      onSuccess: (response: unknown) => {
        // Unwrap BaseResponseDto to get chat room ID
        const data = (response as { data?: { chatRoomId?: string } })?.data;
        const roomId = data?.chatRoomId;

        if (roomId) {
          setChatRoomId(roomId);
          setError(null);
        } else {
          setError(new Error("채팅방 ID를 찾을 수 없습니다"));
        }
      },
      onError: (err: unknown) => {
        setError(
          err instanceof Error
            ? err
            : new Error("견적 제출 실패: 예상치 못한 오류가 발생했습니다"),
        );
        setChatRoomId(null);
      },
      onSettled: () => {
        setIsSubmitting(false);
      },
    },
  });

  const submitQuote = useCallback(async () => {
    if (isSubmitting || chatRoomId) {
      return; // Already submitting or already submitted
    }

    if (!quoteRequest) {
      setError(new Error("견적 요청 정보를 불러오는 중입니다..."));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Auto-generate DTO from URL params + quote request data
      const items = quoteRequest.items.map((item) => ({
        name: item.name,
        quantity: item.quantity,
        unit: item.unit,
        unitPrice: unitPrice,
        amount: item.quantity * unitPrice,
        spec1: item.specifications || "원클릭 제출 (Kakao alimtalk)",
      }));

      const totalAmount = items.reduce((sum, item) => sum + item.amount, 0);

      const dto: CreateQuoteResponseVersionDto = {
        totalAmount,
        items,
        description: "원클릭 제출 (Kakao alimtalk)",
      };

      // Call existing API
      await submitAutoQuoteMutation.mutateAsync({
        quoteRequestId,
        data: dto,
      });
    } catch (err) {
      // Error handled by mutation callbacks
      console.error("Auto quote submission error:", err);
    }
  }, [
    isSubmitting,
    chatRoomId,
    quoteRequest,
    quoteRequestId,
    unitPrice,
    submitAutoQuoteMutation,
  ]);

  return {
    submitQuote,
    isSubmitting,
    isFetching,
    error,
    chatRoomId,
  };
};
